import React from 'react';
import {
    Box,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Typography,
    Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { getBookingStatusColor } from '../helpers/getBookingStatusColor';
import { getOrderStatusColor } from '../helpers/getOrderStatusColor';
import { useSort } from '../../../hooks/useSort';
import SortableHeaderCell from '../../../components/atom/SortableHeaderCell';
import { getTransactionSourceLabel } from '../helpers/getTransactionSourceLabel';

const paymentLabel = (item) =>
    item.transactionPaymentMethodName || item.transactionPaymentType || '';

const TransactionsReportTable = ({ items, reportData, showId, taxBreakout }) => {
    const { t } = useTranslation();
    const showShowColumn = !showId;
    const itemsWithDerived = items.map((item) => ({
        ...item,
        paymentLabel: paymentLabel(item),
        sourceLabel: getTransactionSourceLabel(item, t),
    }));
    const sort = useSort(itemsWithDerived, 'bookingId', 'asc');

    return (
        <Box sx={{ width: '100%' }}>
            <TableContainer sx={{ maxHeight: '65vh' }}>
                <Table stickyHeader sx={{ width: '100%', '& thead th': { top: 0 } }}>
                    <TableHead>
                        <TableRow>
                            <SortableHeaderCell field="bookingId" sort={sort}>
                                {t('reports.columns.bookingId')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="bookingStatus" sort={sort}>
                                {t('reports.columns.bookingStatus')}
                            </SortableHeaderCell>
                            {showShowColumn && (
                                <SortableHeaderCell field="showTitle" sort={sort}>
                                    {t('reports.columns.show', 'Show')}
                                </SortableHeaderCell>
                            )}
                            <SortableHeaderCell field="contact" sort={sort}>
                                {t('reports.columns.contact')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="voucher" sort={sort}>
                                {t('reports.columns.voucher')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="customers" sort={sort}>
                                {t('reports.columns.customers')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="availability" sort={sort}>
                                {t('reports.columns.showDate')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="sourceLabel" sort={sort}>
                                {t('reports.transactions.columns.source')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="orderId" sort={sort}>
                                {t('reports.transactions.columns.orderId')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="ticketAmount" sort={sort} align="right">
                                {t('reports.columns.ticketAmount', 'Ticket amount')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="bookingFees" sort={sort} align="right">
                                {t('reports.columns.bookingFees', 'Booking fees')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="orderAmount" sort={sort} align="right">
                                {t('reports.transactions.columns.orderAmount')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="orderStatus" sort={sort}>
                                {t('reports.transactions.columns.orderStatus')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="transactionId" sort={sort}>
                                {t('reports.transactions.columns.transactionId')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="transactionFinished" sort={sort}>
                                {t('reports.transactions.columns.transactionStatus')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="paymentLabel" sort={sort}>
                                {t('reports.transactions.columns.paymentType')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="transactionDate" sort={sort}>
                                {t('reports.transactions.columns.transactionDate')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="processingFee" sort={sort} align="right">
                                {t('reports.columns.processingFee')}
                            </SortableHeaderCell>
                            <SortableHeaderCell
                                field="absorbedProcessingFee"
                                sort={sort}
                                align="right"
                            >
                                {t('reports.columns.absorbedProcessingFee')}
                            </SortableHeaderCell>
                            {taxBreakout && (
                                <>
                                    <SortableHeaderCell field="netAmount" sort={sort} align="right">
                                        {t('reports.columns.netAmount')}
                                    </SortableHeaderCell>
                                    <SortableHeaderCell field="taxAmount" sort={sort} align="right">
                                        {t('reports.columns.taxAmount')}
                                    </SortableHeaderCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sort.sortedItems.map((item) => (
                            <TableRow key={`${item.bookingId}-${item.transactionId}`}>
                                <TableCell>{item.bookingId}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={t(`bookingstatus.${item.bookingStatus}`)}
                                        color={getBookingStatusColor(item.bookingStatus)}
                                        size="small"
                                    />
                                </TableCell>
                                {showShowColumn && <TableCell>{item.showTitle}</TableCell>}
                                <TableCell>
                                    {item.contact}
                                    <br />
                                    <Typography variant="caption" color="text.secondary">
                                        {item.email}
                                    </Typography>
                                </TableCell>
                                <TableCell>{item.voucher}</TableCell>
                                <TableCell>{item.customers}</TableCell>
                                <TableCell>{item.availability}</TableCell>
                                <TableCell>{item.sourceLabel}</TableCell>
                                <TableCell>{item.orderId}</TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.ticketAmount || 0)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.bookingFees || 0)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.orderAmount)}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={t(`orderstatus.${item.orderStatus}`)}
                                        color={getOrderStatusColor(item.orderStatus)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>{item.transactionId}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={
                                            item.transactionFinished
                                                ? t('reports.transactions.finished')
                                                : t('reports.transactions.pending')
                                        }
                                        color={item.transactionFinished ? 'success' : 'warning'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>{item.paymentLabel}</TableCell>
                                <TableCell>{item.transactionDate}</TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.processingFee || 0)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.absorbedProcessingFee || 0)}
                                </TableCell>
                                {taxBreakout && (
                                    <>
                                        <TableCell align="right">
                                            {formatCentsToUsd(item.netAmount || 0)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatCentsToUsd(item.taxAmount || 0)}
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        ))}

                        {/* Summary Row */}
                        {reportData && (
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                {/* bookingId */}
                                <TableCell>
                                    <strong>{t('reports.summary.total')}</strong>
                                </TableCell>
                                {/* bookingStatus */}
                                <TableCell />
                                {/* show (conditional) */}
                                {showShowColumn && <TableCell />}
                                {/* contact */}
                                <TableCell>
                                    <strong>
                                        {t('reports.summary.bookings', {
                                            count: items.length,
                                        })}
                                    </strong>
                                </TableCell>
                                {/* voucher */}
                                <TableCell />
                                {/* customers */}
                                <TableCell />
                                {/* showDate */}
                                <TableCell />
                                {/* source */}
                                <TableCell />
                                {/* orderId */}
                                <TableCell />
                                {/* ticketAmount */}
                                <TableCell align="right">
                                    <strong>
                                        {formatCentsToUsd(reportData.totalTicketAmount || 0)}
                                    </strong>
                                </TableCell>
                                {/* bookingFees */}
                                <TableCell align="right">
                                    <strong>
                                        {formatCentsToUsd(reportData.totalBookingFees || 0)}
                                    </strong>
                                </TableCell>
                                {/* orderAmount */}
                                <TableCell align="right">
                                    <strong>
                                        {formatCentsToUsd(reportData.totalTransactionAmount || 0)}
                                    </strong>
                                </TableCell>
                                {/* orderStatus */}
                                <TableCell />
                                {/* transactionId */}
                                <TableCell />
                                {/* transactionStatus */}
                                <TableCell />
                                {/* paymentType */}
                                <TableCell />
                                {/* transactionDate */}
                                <TableCell />
                                {/* processingFee */}
                                <TableCell align="right">
                                    <strong>
                                        {formatCentsToUsd(reportData.totalProcessingFees || 0)}
                                    </strong>
                                </TableCell>
                                {/* absorbedProcessingFee */}
                                <TableCell align="right">
                                    <strong>
                                        {formatCentsToUsd(
                                            reportData.totalAbsorbedProcessingFees || 0,
                                        )}
                                    </strong>
                                </TableCell>
                                {taxBreakout && (
                                    <>
                                        <TableCell align="right">
                                            <strong>
                                                {formatCentsToUsd(reportData.totalNetAmount || 0)}
                                            </strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>
                                                {formatCentsToUsd(reportData.totalTaxAmount || 0)}
                                            </strong>
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TransactionsReportTable;
